import { useState } from "react";

import { errorMessage, useSaveSshKeys } from "../../api/queries";
import type { RegisteredKey } from "../../api/types";
import {
  ELSEWHERE_SYNC_NOTE,
  hasKeyFor,
  KEY_SCOPES,
  keyName,
  withScope,
} from "../../lib/keyScopes";
import { readSshPublicKey } from "../../lib/sshKey";
import { SshKeyForm } from "./SshKeyForm";

interface SshKeySectionProps {
  /** The keys the server holds, newest last — the order they were given. */
  storedKeys: RegisteredKey[];
  /** Sentence shown under the keys that are already on file. */
  note?: string;
  /** Heading of the form shown when there is no key yet. */
  formTitle?: string;
  /** Called once the list has been stored, whether a key was added or removed. */
  onSaved?: () => void;
}

/** A change that would leave nothing to open a workspace with. */
interface PendingChange {
  /** The list that would be stored if the researcher goes ahead. */
  keys: RegisteredKey[];
  /** What they were doing, so the warning names it. */
  action: "narrow" | "remove";
  /** The key they were doing it to, named the way its row is. */
  name: string;
}

/**
 * The researcher's SSH keys: which machines they open, and how to change that.
 *
 * An account holds one key per machine — a laptop, a desktop, a cluster login
 * node — because a private key is not meant to travel between them. So this is
 * a list, not a box: every key names its machine through its comment or its
 * label, carries the fingerprint `ssh-keygen -lf` prints for it, says where
 * the console installs it, and can be removed on its own. Removing is sending
 * the others, which is why nobody is ever asked to retype a key they already
 * registered.
 *
 * Two changes never reach the network as they are. Taking the last key off
 * "your workspaces" locks somebody out of their own workspaces without anything
 * on screen looking broken, so it is confirmed **before** it is sent rather
 * than repeated back as a server error afterwards. And unticking a key's *last*
 * place is refused outright, for the same reason the form refuses it and the
 * server after it: a key the console may install nowhere would open nothing.
 * That gesture almost always means "this key should go nowhere at all", whose
 * real answer is Remove — so the refusal says so, because nothing else on the
 * screen does.
 *
 * What *Training elsewhere* does is said **here**, not only in the form that
 * registers a key. Every account arrives from migration 0013 with both places
 * ticked on every key, so the first thing somebody sees is a ticked box for
 * the relay — and most of them will never open the add-a-key form where the
 * sentence about what it opens used to live alone.
 *
 * The same block serves the account screen and the waiting-for-approval
 * screen, because a key can be registered before an account is approved —
 * being ready the second somebody says yes is the point.
 *
 * Args:
 *     storedKeys: The keys currently on file.
 *     note: Sentence shown under the stored keys.
 *     formTitle: Heading of the form when no key is on file.
 *     onSaved: Called once the list has been stored.
 *
 * Returns:
 *     Either the keys on file with a way to change them, or the form.
 */
export function SshKeySection({
  storedKeys,
  note,
  formTitle,
  onSaved,
}: SshKeySectionProps): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState("");
  const save = useSaveSshKeys();

  if (storedKeys.length === 0) {
    return (
      <>
        <p className="form__note">
          No key on file — that is the only thing between you and a shell. Add
          the public key of the machine you connect from. One key per machine is
          the safe habit: your laptop and a cluster login node each get their
          own, and the private half never leaves the machine that made it.
        </p>
        <SshKeyForm
          title={formTitle}
          onSaved={() => {
            setAdding(false);
            onSaved?.();
          }}
        />
      </>
    );
  }

  const send = (keys: RegisteredKey[]): void => {
    setPending(null);
    setRefused(null);
    setRenaming(null);
    save.mutate({ ssh_public_keys: keys }, { onSuccess: () => onSaved?.() });
  };

  /**
   * Store a new list, unless the change is one of the two never sent as it is.
   *
   * A key ticked nowhere is refused here rather than by the server, which
   * refuses the whole list and would leave the researcher reading a `400`
   * about a box they can see. Losing the last key for workspaces is confirmed
   * instead of refused: it is their decision to make, only not by accident.
   *
   * Args:
   *     keys: The list that would replace the one on file.
   *     action: What the researcher did, so the warning can name it.
   *     name: The key they did it to.
   */
  const apply = (
    keys: RegisteredKey[],
    action: PendingChange["action"],
    name: string,
  ): void => {
    setRefused(null);
    // Refused here for the same reason the server refuses it, and the same
    // reason the add-a-key form does: a key the console may install nowhere
    // would open nothing. Unticking both boxes is the obvious way to say
    // "this key should go nowhere" — and the answer to that is Remove, which
    // nothing else on this screen offers as the answer to anything.
    if (keys.some((key) => key.scopes.length === 0)) {
      setPending(null);
      setRefused(
        `“${name}” has to be installed somewhere: a key the console may put ` +
          "nowhere would open nothing, so it is not a narrower key — it is a " +
          "row nobody can explain. If this key should go nowhere at all, " +
          "Remove it instead.",
      );
      return;
    }
    if (hasKeyFor(storedKeys, "workspaces") && !hasKeyFor(keys, "workspaces")) {
      setPending({ keys, action, name });
      return;
    }
    send(keys);
  };

  const replaceAt = (index: number, key: RegisteredKey): RegisteredKey[] =>
    storedKeys.map((current, position) => (position === index ? key : current));

  const rows = storedKeys.map((key) => {
    const { algorithm, fingerprint, comment } = readSshPublicKey(key.public_key);
    return {
      key,
      algorithm,
      fingerprint,
      comment,
      name: keyName(key.label, comment),
    };
  });

  // Two keys with neither a label nor a comment are both called "This key
  // names no machine", and a screen reader would then announce two identical
  // checkboxes and two identical Remove buttons. The fingerprint is what tells
  // them apart — it is already on the row, and it is unique by construction.
  // Only the ambiguous rows carry it, so the common case stays short to hear.
  const twiceNamed = new Set(
    rows
      .map((row) => row.name)
      .filter((name, position, all) => all.indexOf(name) !== position),
  );

  /**
   * How a row is announced when it is not being looked at.
   *
   * Args:
   *     position: Where the key sits in the list.
   *
   * Returns:
   *     The name, plus what distinguishes it when another row shares it.
   */
  const spokenName = (position: number): string => {
    const row = rows[position]!;
    if (!twiceNamed.has(row.name)) {
      return row.name;
    }
    return `${row.name}, ${row.fingerprint || `key ${position + 1}`}`;
  };

  return (
    <>
      <p className="form__note">
        A tick says where the console may put that key — not what the key may do
        once it is there. Ticked for “Your workspaces”, a key goes into every
        workspace you open from now on. Ticked for “Training elsewhere”, it is
        meant for the machine off the cluster that reaches MLflow and Jean Zay —{" "}
        {ELSEWHERE_SYNC_NOTE}
      </p>

      <ul className="ssh-keys">
        {rows.map(({ key, algorithm, fingerprint, comment, name }, index) => {
          const spoken = spokenName(index);
          return (
            <li className="ssh-keys__row" key={key.public_key}>
              <div className="ssh-keys__identity">
                <span className="ssh-keys__machine mono">{name}</span>
                <span className="ssh-keys__fingerprint mono">
                  {[algorithm, fingerprint].filter(Boolean).join(" · ")}
                </span>
                {key.label && comment ? (
                  <span className="ssh-keys__comment mono">{comment}</span>
                ) : null}
              </div>

              <div className="ssh-keys__actions">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={save.isPending}
                  onClick={() => {
                    setRenaming(key.public_key);
                    setDraftLabel(key.label ?? "");
                  }}
                >
                  {key.label ? "Rename" : "Name this machine"}
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={save.isPending}
                  aria-label={`Remove the key ${spoken}`}
                  onClick={() =>
                    apply(
                      storedKeys.filter((_, position) => position !== index),
                      "remove",
                      name,
                    )
                  }
                >
                  Remove
                </button>
              </div>

              <fieldset className="ssh-keys__scopes" disabled={save.isPending}>
                <legend className="ssh-keys__scopes-legend">
                  Installed on
                </legend>
                {KEY_SCOPES.map((scope) => (
                  <label className="ssh-keys__scope" key={scope.value}>
                    <input
                      type="checkbox"
                      checked={key.scopes.includes(scope.value)}
                      aria-label={`${scope.label}, ${spoken}`}
                      onChange={(event) =>
                        apply(
                          replaceAt(
                            index,
                            withScope(key, scope.value, event.target.checked),
                          ),
                          "narrow",
                          name,
                        )
                      }
                    />
                    <span>{scope.label}</span>
                  </label>
                ))}
              </fieldset>

              {renaming === key.public_key ? (
                <div className="ssh-keys__rename">
                  <label className="sr-only" htmlFor="ssh-key-rename">
                    What do you call this machine?
                  </label>
                  <input
                    id="ssh-key-rename"
                    className="input"
                    type="text"
                    maxLength={60}
                    value={draftLabel}
                    autoComplete="off"
                    placeholder="the desktop under the window"
                    onChange={(event) => setDraftLabel(event.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn--sm btn--primary"
                    disabled={save.isPending}
                    onClick={() =>
                      send(
                        replaceAt(index, {
                          ...key,
                          label: draftLabel.trim() || null,
                        }),
                      )
                    }
                  >
                    Save name
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost"
                    onClick={() => setRenaming(null)}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {pending ? (
        <div className="ssh-keys__warning" role="alert">
          <p>
            {pending.action === "narrow"
              ? `Untick “Your workspaces” on ${pending.name} and nothing will let you into a workspace: `
              : `Remove ${pending.name} and nothing will let you into a workspace: `}
            it is the only key you have for them. Add another key for your
            workspaces first, or leave this one as it is. A workspace already
            running keeps the file it started with — until it is stopped,
            resized or placed on another machine, when it gets the new list.
          </p>
          <div className="ssh-keys__actions">
            <button
              type="button"
              className="btn btn--sm"
              disabled={save.isPending}
              onClick={() => send(pending.keys)}
            >
              {pending.action === "narrow" ? "Untick it anyway" : "Remove it anyway"}
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => setPending(null)}
            >
              Keep it as it is
            </button>
          </div>
        </div>
      ) : null}

      {refused ? (
        <p className="form__error" role="alert">
          {refused}
        </p>
      ) : null}

      {save.isError ? (
        <p className="form__error" role="alert">
          {errorMessage(save.error)}
        </p>
      ) : null}

      <p className="form__note">
        Changing where a key goes takes effect at the next workspace you open.
        For training elsewhere it is faster: the relay machine reads this list
        about once a minute, so a key ticked for it opens that machine — and an
        unticked one stops opening it — within a minute or two. A workspace
        already running keeps the file it started with — stop it and open it
        again if you need a key gone from there now.
      </p>

      {adding ? (
        <SshKeyForm
          existingKeys={storedKeys}
          title="Add the key of another machine"
          onSaved={() => {
            setAdding(false);
            onSaved?.();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setAdding(true)}
          >
            Add another key
          </button>
          <p className="form__note">
            One key per machine, so nothing has to be copied between them: the
            private half stays where it was made.
          </p>
        </>
      )}

      {note ? <p className="form__note">{note}</p> : null}
    </>
  );
}
